import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, Section } from '~/components';
import { RelativeTime } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardToken, CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

import { CardProps } from './Card';

const columns: TableColumn<CardToken>[] = [
  {
    header: 'Token ID',
    accessor: 'id',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => (
      <Table.TextCell displayVariant="monospaced" weight="medium" value={(value as string) || '-'} />
    ),
  },
  {
    header: 'Wallet Type',
    accessor: 'walletType',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
  },
  {
    header: 'Token Type',
    accessor: 'tokenType',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
  },
  {
    header: 'Device Name',
    accessor: 'deviceName',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={(value as string) || '-'} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.Cell>{value ? <RelativeTime timestamp={value as Date} /> : '-'}</Table.Cell>,
  },
];

export const PageCardTokens: React.FC = () => {
  const navigate = useNavigate();
  const { card } = useOutletContext<CardProps>();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [card?.id, currentPlatform?.id, isSandbox, resetPagination]);

  const { data: tokensData, isLoading } = useQuery({
    queryKey: ['card-tokens', 'by-card', card?.id, currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardTokenRepository.listCardTokens({ cardId: card.id, limit: pageSize, startingAfter: cursor }),
    enabled: !!card?.id,
    placeholderData: keepPreviousData,
  });
  const cardTokens = tokensData?.cardTokens ?? [];
  const hasMore = Boolean(tokensData?.hasMore);

  return (
    <Inner p={0}>
      <Section headline="Tokens">
        <Loading isLoading={isLoading}>
          <Inner px="var(--page-margin)" py={0}>
            {cardTokens.length === 0 && !hasPrevious ? (
              <EmptyState headline="No tokens yet" text="Tokens will appear here for this card." />
            ) : (
              <Table<CardToken>
                columns={columns}
                data={cardTokens}
                variant={TableVariant.List}
                pagination={{
                  hasNext: hasMore,
                  hasPrevious,
                  pageIndex,
                  pageSize,
                  onNext: () => {
                    if (cardTokens.length > 0) {
                      onNext({ cursor: cardTokens[cardTokens.length - 1].id });
                    }
                  },
                  onPrevious,
                  onPageSizeChange: setPageSize,
                }}
                onRowClick={(row) => {
                  navigate(`${ROUTE.CARD_TOKENS}/edit/${row.id}`);
                }}
              />
            )}
          </Inner>
        </Loading>
      </Section>
    </Inner>
  );
};
